// Contracts — kids cabinet shared enums (2026-06-28).
// Single source of the domain unions the cabinet endpoints validate against.
// Stored as TEXT in weern_kids (migrations 0001/0002); these zod enums keep the
// worker, contracts, and frontend aligned so the schema can't drift. Ported from
// the old kids-worker lib/enums.ts (Hono+drizzle) onto pure zod.
import { z } from 'zod';

// ── roles ──────────────────────────────────────────────────────────────────
// reception = office front-desk (check-in only). Multi-role lives in user_roles;
// users.role stays the PRIMARY (highest) role.
export const KidsRoleSchema = z.enum([
  'parent',
  'reception',
  'moderator',
  'admin',
  'manager',
]);
export type KidsRole = z.infer<typeof KidsRoleSchema>;
// privilege order (low → high); "highest role wins" scoping.
export const KIDS_ROLE_RANK: Record<KidsRole, number> = {
  parent: 0,
  reception: 1,
  moderator: 2,
  admin: 3,
  manager: 4,
};
// staff = anyone who can run the shoot pipeline (check-in / shot).
export const KIDS_STAFF_ROLES: readonly KidsRole[] = ['moderator', 'reception', 'admin', 'manager'];

// ── moderator PID (shooting ID) ranges by cluster ────────────────────────────
// Tim's rule (2026-06-30): "ID у нас для Atlanta 11-50 СТРОГО, для Remote СТРОГО
// от 50". Atlanta PIDs are handed out MANUALLY by us (admin in user-edit) and must
// land in 11..50 inclusive. Remote PIDs are self-assigned (POST /me/assign-pid,
// nextFreePid from 50) and must be ≥50. The two ranges touch at 50 — that is
// intentional (both bounds inclusive; a manual '50' is a valid Atlanta PID, a
// self-assigned '50' is the first free Remote PID). Single source of truth for
// both the worker (handleAdminUserUpdate validation) and the frontend hints.
export const ATLANTA_PID_MIN = 11;
export const ATLANTA_PID_MAX = 50;
export const REMOTE_PID_MIN = 50;
export const PID_MAX = 99; // 2-digit pool ceiling (matches nextFreePid loop)

/**
 * Whether a 2-digit PID is valid for a cluster. Pure/deterministic — the worker
 * validates the admin-set PID against the target office's cluster; the frontend
 * mirrors it for inline hints. `pid` is the raw 2-digit string ('05'..'99'); a
 * non-numeric value returns false.
 *
 *   'atlanta' → 11..50   'remote' → 50..99
 */
export function pidInRangeForCluster(cluster: 'atlanta' | 'remote', pid: string): boolean {
  const n = Number(pid);
  if (!Number.isInteger(n)) return false;
  if (cluster === 'atlanta') return n >= ATLANTA_PID_MIN && n <= ATLANTA_PID_MAX;
  return n >= REMOTE_PID_MIN && n <= PID_MAX;
}

// ── office payout rate by cluster ────────────────────────────────────────────
// Tim's rule (2026-07-01): "ставка офиса функция для remote, у атланты дефолт 40
// не меняется". Atlanta offices pay a FIXED participant rate of $40 that is not
// editable (the finance rate editor is a Remote-only feature). Remote offices
// keep the editable per-office rate. The rate is stored/served in INTEGER MINOR
// UNITS (cents), so $40 = 4000. Single source of truth for the worker
// (handleOfficeRates / handleOfficeRateUpsert / debitForDoneSession) and the
// frontend (the rate row renders locked for Atlanta).
export const ATLANTA_FIXED_RATE_CENTS = 4000; // $40.00, fixed & non-editable

/** True when this cluster's office rate is fixed (Atlanta) rather than editable
 *  (Remote). Pure/deterministic — used to lock the rate editor + reject upserts. */
export function officeRateIsFixed(cluster: 'atlanta' | 'remote' | null): boolean {
  return cluster === 'atlanta';
}

// ── people / children ──────────────────────────────────────────────────────
// KidsGenderSchema / KidsEthnicitySchema already live in ../kids.ts (the public
// lead form) — re-exported there, reused here to avoid a duplicate definition.

// ── meeting status ───────────────────────────────────────────────────────────
// meetings.status is a plain TEXT column (migrations/0002:108, DEFAULT
// 'scheduled', NO CHECK constraint) — so adding `rejected` needs NO migration.
// Single source of truth for the reception board + check-in flow.
//   scheduled  — booked, family not yet arrived
//   checked_in — family arrived, kids can be picked up by any moderator
//   done       — DERIVED by the shot flow (F1) when a session finishes; reception
//                must NOT set this directly
//   canceled   — booking dropped (frees the slot; slots.ts excludes it)
//   rejected   — family arrived but was turned away / no-show; frees the slot
//                EXACTLY like canceled (slots.ts excludes both) — Tim 2026-07-01
export const KidsMeetingStatusSchema = z.enum([
  'scheduled',
  'checked_in',
  'done',
  'canceled',
  'rejected',
]);
export type KidsMeetingStatus = z.infer<typeof KidsMeetingStatusSchema>;

// ── sessions ───────────────────────────────────────────────────────────────
export const KidsSessionStatusSchema = z.enum(['started', 'done', 'canceled']);
export type KidsSessionStatus = z.infer<typeof KidsSessionStatusSchema>;

// ── face expressions ───────────────────────────────────────────────────────
// 12-value set (Tim, 2026-06-30). The stored/exported value is the canonical
// slug; the RU label is display-only (the packer consumes the slug, not the
// label). Order is the UI order. Kept in sync with apps/kids-api/src/enums.ts
// (re-export) and the frontend mirror apps/kids-web/src/lib/enums.ts by hand.
export const KIDS_FACE_EXPRESSIONS = [
  'neutral',
  'happy',
  'surprised',
  'curious',
  'focused',
  'confused',
  'sad',
  'angry',
  'scared',
  'disgusted',
  'crying',
  'silly',
] as const;
export const KidsFaceExpressionSchema = z.enum(KIDS_FACE_EXPRESSIONS);
export type KidsFaceExpression = z.infer<typeof KidsFaceExpressionSchema>;
export const KIDS_DEFAULT_FACE_EXPRESSION: KidsFaceExpression = 'neutral';

// ── lighting ───────────────────────────────────────────────────────────────
// Order IS the capture order (step). Lux ranges mirror the captures lux check so
// UI and validation can't drift. Ported verbatim from the old enums.ts.
// Order MUST match apps/kids-api/src/enums.ts LIGHTINGS (the capture order and
// the packer's step order): dim(1) → normal(2) → bright(3) → backlight(4).
// Keep them in lockstep — a swap here silently reorders any UI that derives
// slot order from the enum options.
export const KidsLightingSchema = z.enum([
  'dim_natural',
  'normal_natural',
  'bright_artificial',
  'backlight_artificial',
]);
export type KidsLighting = z.infer<typeof KidsLightingSchema>;
